/**
 * app-messaging-context.js
 */

import React from 'react';
import { randomString } from 'utils/random';
import { UserContext } from 'contexts/user/user-context';


const RN_WEBVIEW_MESSAGE_KEY = 'hD4witfubkSCvsCBokXXOg';

// mirrored in app
const APP_MESSAGE_TYPE = {
  APP_INFO: 'app-info',
  HAS_PASSCODE: 'has-passcode',
  SET_PASSCODE: 'set-passcode',
  SET_PASSCODE_TOKEN: 'set-passcode-token',
  PASSCODE_LOGIN: 'passcode-login',
  LOGIN_STARTED: 'login-started',
  GET_LOCATION: 'get-location',
  GET_TRACKING_STATE: 'get-tracking-state',
  SET_TRACKING_STATE: 'set-tracking-state',
  SET_COMPASS_STATE: 'set-compass-state',
  GET_LOCATION_PLUGIN_DEBUG: 'get-location-debug'
};

// mirrored in app
export const APP_SUBSCRIPTION_TYPE = {
  LOCATION: 'location',
  APP_STATE: 'app-state', // NOTE: this subscription causes strange behavior (app's AppState event is faulty). use with extreme caution.
  HEADING: 'heading',
  MODAL_WEBVIEW_CLOSED: 'modal-webview-closed',
};

// mirrored in app
export const APP_TRACKING_STATE = {
  DISABLED: 'disabled', // no location updates are happening
  ENABLED: 'enabled', // location updates are enabled but we aren't tracking
  TRACKING: 'tracking' // tracking in progress
};


export const AppMessagingContext = React.createContext();


export const AppMessagingProvider = (props) => {
  const {
    children,
  } = props;
  const { user } = React.useContext(UserContext);
  const sentMessages = React.useRef([]);
  const subscriptions = React.useRef([]);
  const appVersion = React.useRef('');
  const [state, setState] = React.useState({
    appInfo: null,
  });


  // subscribes to app message
  const subscribe = React.useCallback((event, handler) => {
    if (!subscriptions.current.find(sub => sub.event === event && sub.handler === handler)) {
      subscriptions.current.push({ event, handler });
    }
  }, []);


  // unsubscribes from app message
  const unsubscribe = React.useCallback((event, handler) => {
    subscriptions.current = subscriptions.current.filter(sub => !(sub.event === event && sub.handler === handler));
  }, []);


  // sends a one-time message to app and returns its response
  const sendMessage = React.useCallback((event, params) => {
    const id = randomString(16);
    const promise = new Promise((resolve, reject) => {
      if (window.ReactNativeWebView) {
        (async () => {
          // look for existing event for this type and wait for it to complete before sending a new one
          const existingEventMessage = sentMessages.current.find(m => m.event === event);
          if (existingEventMessage && existingEventMessage.promise) {
            await existingEventMessage.promise;
          }
          sentMessages.current.push({ id, event, handler: resolve });
          window.ReactNativeWebView.postMessage(JSON.stringify({
            key: RN_WEBVIEW_MESSAGE_KEY,
            id,
            event,
            params
          }));
        })();
      } else {
        resolve();
      }
    });

    // find the newly-created message and add the promise object
    const message = sentMessages.current.find(m => m.id === id);
    if (message) {
      message.promise = promise;
    }
    return promise;
  }, []);

  
  // INIT: setup main event handler 
  React.useEffect(() => {
    if (window.ReactNativeWebView) {
      // define generic message handler
      const onMessage = (messageEvent) => {
        const message = messageEvent.data;
        const id = message?.id;
        const event = message?.event;
        const params = message?.params;
  
        // ensure this is an app communication message
        if (!message || message.key !== RN_WEBVIEW_MESSAGE_KEY) {
          return;
        }


        // support older app versions (4.6.1 or below)
        // TODO: remove this when apps are upgraded
        if (!id && (event === 'app-info' || appVersion.current <= '4.6.1')) {
          const sentMessage = sentMessages.current.find(m => m.event === event);
          if (sentMessage) {
            sentMessages.current = sentMessages.current.filter(m => !(m.event === event));
            sentMessage.handler(params);
          }
          return;
        }


        if (id) {
          // message is in response to a sent message
          const sentMessage = sentMessages.current.find(m => m.id === id && m.event === event);
          if (sentMessage) {
            sentMessage.handler(params);
            sentMessages.current = sentMessages.current.filter(m => !(m.id === id && m.event === event));
          }
        } else {
          // subscription -> pass params to all relevant event handlers
          subscriptions.current
            .filter(sub => sub.event === event)
            .forEach(sub => sub.handler(params));
        }
      }
  
      window.addEventListener('message', onMessage);
      return () => window.removeEventListener('message', onMessage);
    }
  }, []);


  // INIT: get app info
  React.useEffect(() => {
    (async () => {
      const appInfo = await sendMessage(APP_MESSAGE_TYPE.APP_INFO) ?? null;
      appVersion.current = appInfo?.version;
      setState(p => ({ ...p, appInfo }));
    })();
  }, [sendMessage]);


  // INIT: get has passcode
  React.useEffect(() => {
    (async() => {
      const hasPasscode = await sendMessage(
        APP_MESSAGE_TYPE.HAS_PASSCODE, 
        user ? { userId: user.id } : { userId: 0 });
      setState(p => ({ ...p, hasPasscode }));
    })();
  }, [user, sendMessage]);


  // sets the user's app passcode
  const setPasscode = React.useCallback(async (passcode, token = null) => {
    if (user) {
      await sendMessage(APP_MESSAGE_TYPE.SET_PASSCODE, { userId: user.id, value: passcode, token });
      setState(p => ({ ...p, hasPasscode: Boolean(passcode) }));
      return true;
    } else {
      return false;
    }
  }, [user, sendMessage]);


  // sets access token associated with user and passcode
  const setPasscodeToken = React.useCallback(async (token) => {
    if (user) {
      // temp support older app versions
      const eventName = state.appInfo?.appVersion <= '4.6.1' ? 'update-token' : APP_MESSAGE_TYPE.SET_PASSCODE_TOKEN;
      
      await sendMessage(eventName, { userId: user.id, token });
      return true;
    } else {
      return false;
    }
  }, [user, state.appInfo, sendMessage]);


  // attempts a passcode login using the given passcode
  const passcodeLogin = React.useCallback(async (passcode) => {
    return await sendMessage(APP_MESSAGE_TYPE.PASSCODE_LOGIN, { passcode });
  }, [sendMessage]);


  // login started. used to show loading spinner in app (may not be necessary anymore...)
  const loginStarted = React.useCallback(async () => {
    return await sendMessage(APP_MESSAGE_TYPE.LOGIN_STARTED);
  }, [sendMessage]);


  // get location.
  // context: address (accuracy 100m)
  // context: tracking (accuracy 250m)
  const getLocation = React.useCallback(async (context) => {
    // supporting app version 4.6.1 alt event name.
    const eventName = appVersion.current <= '4.6.1' ? 'coordinates' : APP_MESSAGE_TYPE.GET_LOCATION;
    return await sendMessage(eventName, { context });
  }, [sendMessage]);


  // get tracking state
  const getTrackingState = React.useCallback(async () => {
    return await sendMessage(APP_MESSAGE_TYPE.GET_TRACKING_STATE);
  }, [sendMessage]);


  // set tracking state. see available states above.
  const setTrackingState = React.useCallback(async (state, params = null) => {
    return await sendMessage(APP_MESSAGE_TYPE.SET_TRACKING_STATE, { state, params });
  }, [sendMessage]);


  // compass heading
  const setCompassState = React.useCallback(async (isEnabled, updateThresholdDegrees = 5) => {
    return await sendMessage(APP_MESSAGE_TYPE.SET_COMPASS_STATE, { isEnabled, updateThresholdDegrees });
  }, [sendMessage]);


  // location plugin debug info
  const getLocationPluginDebug = React.useCallback(async () => {
    return await sendMessage(APP_MESSAGE_TYPE.GET_LOCATION_PLUGIN_DEBUG);
  }, [sendMessage]);


  const value = React.useMemo(() => ({
    ...state,
    subscribe,
    unsubscribe,
    setPasscode,
    setPasscodeToken,
    passcodeLogin,
    loginStarted,
    getLocation,
    getTrackingState,
    setTrackingState,
    setCompassState,
    getLocationPluginDebug
  }), [
    state,
    subscribe,
    unsubscribe,
    setPasscode,
    setPasscodeToken,
    passcodeLogin,
    loginStarted,
    getLocation,
    getTrackingState,
    setTrackingState,
    setCompassState,
    getLocationPluginDebug
  ]);

  
  return (<>
    <AppMessagingContext.Provider 
      value={value}
      children={children}
    />
  </>);
}